import type { Agent, AgentReadCursors, ColorName, Persisted } from '@tunasong/schemas'
import { getColorCSS, useUserConfig } from '@tunasong/ui-lib'
import { useEffect } from 'react'
import { getDistinctInitials } from './agent-initials.js'
import { deriveAgentRuntimeStatus } from './agent-runtime-status.js'
import { getAgentDisplayName, resolveAgentVisuals } from './agent-visuals.js'
import { computeClearedCursors } from './clear-agent-unread.js'
import { useAgentChildren } from './use-agent-children.js'
import { deriveAgentUnread } from './use-agent-unread.js'

export interface AgentRosterRow {
  agent: Persisted<Agent>
  name: string
  initials: string
  color: string
  unread: boolean
  /** Spoken status suffix: "unread", "thinking", or the error message. */
  status: string[]
}

export interface AgentRoster {
  rows: AgentRosterRow[]
  /** Agents with unread activity (not messages: unread is one flag per agent) */
  unreadCount: number
  isThinking: boolean
  /** Mark an agent's activity as seen (bumps its read cursor) */
  acknowledge: (agent: Persisted<Agent>) => void
}

/**
 * The agents attached to a host entity, with their unread and runtime status. Owns the first-visit
 * read-cursor stamp, so mount it exactly once per host wherever the agents are surfaced (the header
 * pill on wide screens, the More menu on small ones).
 */
export const useAgentRoster = (parentId?: string | null): AgentRoster => {
  const agents = useAgentChildren(parentId)
  const [cursors, updateCursors, configLoaded] = useUserConfig<'agentReadCursors'>('agentReadCursors')
  const cursorMap = cursors as AgentReadCursors | undefined

  // First-visit suppression: stamp a cursor for every agent the user has never seen, in one write,
  // so already-old activity does not read as unread. Gated on configLoaded (TUNASONG-VQ / #1337).
  useEffect(() => {
    if (!configLoaded || agents.length === 0) {
      return
    }
    const missing = agents.filter(agent => !cursorMap?.[agent.id])
    if (missing.length === 0) {
      return
    }
    const next = missing.reduce<AgentReadCursors | undefined>(
      (acc, agent) => computeClearedCursors(agent.id, acc),
      cursorMap
    )
    updateCursors(next as never)
  }, [agents, configLoaded, cursorMap, updateCursors])

  const names = agents.map(getAgentDisplayName)
  const initials = getDistinctInitials(names)
  const rows: AgentRosterRow[] = agents.map((agent, i) => {
    const unread = deriveAgentUnread(agent, cursorMap?.[agent.id])
    const runtimeStatus = deriveAgentRuntimeStatus(agent)
    const status = [
      ...(runtimeStatus === 'error' ? [agent.runtimeError?.trim() || 'error'] : []),
      ...(runtimeStatus === 'thinking' ? ['thinking'] : []),
      ...(unread ? ['unread'] : []),
    ]
    return {
      agent,
      name: names[i],
      initials: initials[i],
      color: getColorCSS(resolveAgentVisuals(agent).color as ColorName),
      unread,
      status,
    }
  })

  const acknowledge = (agent: Persisted<Agent>) => updateCursors(computeClearedCursors(agent.id, cursorMap) as never)

  return {
    rows,
    unreadCount: rows.filter(row => row.unread).length,
    isThinking: rows.some(row => row.status.includes('thinking')),
    acknowledge,
  }
}
